"use client";

/**
 * 앱 딥링크 — 같은 경로를 앱에서 열고, 앱이 없으면 스토어로 보낸다.
 * @description `[...path]` 리다이렉터가 쓰던 것을 꺼내 공유한다. 콜랩 상세처럼 "앱에서
 *   보기" 를 권하는 자리가 늘면서 같은 규칙을 두 번 적게 됐다.
 */
import { STORE_URLS } from "@/shared/config/site";

const ANDROID_PACKAGE = "kr.fanwork.nativeapp";
const CUSTOM_SCHEME = "fanwork://";

/** 앱이 안 열렸다고 보고 스토어로 넘기기까지 기다리는 시간. */
const FALLBACK_DELAY_MS = 1500;

export type MobileOS = "ios" | "android" | "unknown";

export function getOS(): MobileOS {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  return "unknown";
}

/**
 * 앱을 여는 주소.
 * @description 안드로이드는 `intent://` 를 쓴다 — 카카오톡·인스타그램 같은 인앱 브라우저에서
 *   커스텀 스킴보다 잘 열린다.
 */
export function buildDeepLink(path: string, os: MobileOS): string {
  if (os === "android") {
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `intent://${cleanPath}#Intent;scheme=fanwork;package=${ANDROID_PACKAGE};end`;
  }
  return `${CUSTOM_SCHEME}${path}`;
}

export function getStoreUrl(os: MobileOS): string {
  return os === "android" ? STORE_URLS.android : STORE_URLS.ios;
}

/**
 * 앱으로 열고, 안 열리면 스토어로 보낸다.
 * @description 앱 설치 여부를 물어볼 방법이 없으므로 열어 보고 기다리는 수밖에 없다.
 *   앱이 뜨면 이 탭은 배경으로 밀려 타이머가 멈추거나 사용자가 돌아오지 않고, 안 뜨면
 *   그대로 남아 스토어로 넘어간다.
 *
 *   `[...path]` 리다이렉터가 쓰던 방식과 같다 — 같은 규칙을 두 군데 적지 않으려고 꺼냈다.
 */
export function openInApp(path: string): void {
  const os = getOS();
  const timer = window.setTimeout(() => {
    window.location.href = getStoreUrl(os);
  }, FALLBACK_DELAY_MS);

  // 탭이 가려지면 앱이 뜬 것이다 — 그때는 스토어로 보내지 않는다.
  const cancel = () => {
    if (document.visibilityState === "hidden") window.clearTimeout(timer);
  };
  document.addEventListener("visibilitychange", cancel, { once: true });

  window.location.href = buildDeepLink(path, os);
}
